import './ContextAnalysisPage.scss';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import React, { useCallback, useMemo, useState } from 'react';
import Title from '../../shared/components/title/Title';
import { useTranslation } from 'react-i18next';
import { PROJECTS } from '../../shared/routes/path';
import FilterPanel from '../../shared/components/filter-panel/FilterPanel';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import useProjectNavigate from '../../shared/hooks/projects/preview/UseProjectNavigate';
import SentimentAnalysis from './components/sentiment-analysis/SentimentAnalysis';
import { useFilters } from '../../shared/contexts/filters/FiltersContext';
import { PeriodType } from '../../shared/types/prompt/PeriodType';
import { FilterItem } from '../../shared/components/filter-box/models/FilterBoxModel';
import { ChatType } from '../../shared/types/monitor/ChatType';
import {
  expandSelectedChatTypes,
  getMonitorTypesForChatType,
} from '../../shared/helpers/MonitorTypeHelpers';
import {
  ContextInsightsParams,
  useGetContextInsights,
} from '../../shared/api/hooks/context-insights/UseGetContextInsights';
import { useGetProject } from '../../shared/api/hooks/project/useGetProject';
import KeyTopics from './components/key-topics/KeyTopics';
import TopicShare from './components/topic-share/TopicShare';
import BrandStrength from './components/brand-strength/BrandStrength';
import ContextGap from './components/context-gap/ContextGap';
import { useGetCommonKeywords } from '../../shared/api/hooks/monitor/keyword/useGetCommonKeywords';
import { useGetEvents } from '../../shared/api/hooks/monitor/events/useGetEvents';
import { useProjectDates } from '../projects/preview/hooks/UseProjectDates';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { useGetContextInsightPeriods } from '../../shared/api/hooks/context-insights/UseGetContextInsightPeriods';
import { usePromptGroups } from '../../shared/hooks/prompt/UsePromptGroups';
import { useGetMonitorsForProject } from '../../shared/api/hooks/monitor/useGetMonitorsForProject';
import {
  exportContextBrandPresenceCsv,
  exportContextTopicsAnalysisCsv,
} from '../../shared/api/services/context-insights/ContextInsightsService';
import { format } from 'date-fns';
import { buildBucketLabelFormatters } from '../../shared/helpers/date/bucketDateLabels';
import { downloadBlobAsFile } from '../../shared/utils/utils';
import useExport from '../../shared/hooks/global/UseExport';

const ContextAnalysisPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { projectId } = useParams();
  const location = useLocation();

  const [selectedInsightPeriod, setSelectedInsightPeriod] = useState<string | null>(null);
  const [savedDefaults, setSavedDefaults] = useState<{
    range: (Date | null)[] | null;
    monitorPeriod: PeriodType | null;
    chatType: ChatType | null;
  } | null>(null);
  const [isDownloadingTopicsAnalysis, setIsDownloadingTopicsAnalysis] = useState(false);
  const [isDownloadingBrandPresence, setIsDownloadingBrandPresence] = useState(false);

  const projectIdNumber = Number(projectId);
  const { accountResponse } = useAccount();
  const { canExport } = useExport();

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedGroupId = useMemo(() => {
    const param = searchParams.get('group');
    if (!param) return -1;
    const parsed = Number(param);
    return isNaN(parsed) ? -1 : parsed;
  }, [searchParams]);
  const setSelectedGroupId = useCallback((groupId: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (groupId === -1) {
        next.delete('group');
      } else {
        next.set('group', String(groupId));
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const hasPromptGroupsAddon = !!accountResponse?.addons?.includes('PROMPT_GROUP');
  const { data: promptGroups } = usePromptGroups(projectIdNumber);
  const { monitorPrompts } = useGetMonitorsForProject(projectIdNumber);

  const filteredMonitorPromptIds = useMemo(() => {
    if (!monitorPrompts || selectedGroupId === -1) return undefined;
    return monitorPrompts
      .filter((p) => p.promptGroupId === selectedGroupId)
      .map((p) => p.id)
      .filter((id): id is number => id != null);
  }, [monitorPrompts, selectedGroupId]);

  const nonEmptyPromptGroups = useMemo(() => {
    if (!promptGroups || !monitorPrompts) return [];
    const groupIdsWithPrompts = new Set(
      monitorPrompts
        .map((p) => p.promptGroupId)
        .filter((id): id is number => id != null)
    );
    return promptGroups.filter((g) => groupIdsWithPrompts.has(g.id));
  }, [promptGroups, monitorPrompts]);

  const groupFilterOptions = useMemo(() => {
    if (!hasPromptGroupsAddon || nonEmptyPromptGroups.length === 0) return [];
    return [
      { label: t('allGroups'), value: -1 },
      ...nonEmptyPromptGroups.map((g) => ({ label: g.name, value: g.id })),
    ];
  }, [hasPromptGroupsAddon, nonEmptyPromptGroups, t]);

  const { selectedChatType, selectedChatTypes, monitorPeriod, range, setRange, setMonitorPeriod, setSelectedChatType } = useFilters();
  const { startDate, endDate } = useProjectDates();

  const { events: monitorEvents } = useGetEvents(
    projectIdNumber,
    monitorPeriod,
    selectedChatType,
    startDate,
    endDate
  );

  const hasExplicitModelFilter = searchParams.get('models') !== null;
  const filterMonitorTypes = useMemo(() => {
    if (!hasExplicitModelFilter || !selectedChatTypes || selectedChatTypes.length === 0) return undefined;
    const expandedChatTypes = expandSelectedChatTypes(selectedChatTypes);
    return Array.from(expandedChatTypes).flatMap((ct) => getMonitorTypesForChatType(ct as ChatType));
  }, [hasExplicitModelFilter, selectedChatTypes]);

  const handleInsightPeriodChange = (periodValue: string | null) => {
    if (periodValue) {
      if (!selectedInsightPeriod) {
        setSavedDefaults({
          range: range ? [...(range as (Date | null)[])] : null,
          monitorPeriod,
          chatType: selectedChatType,
        });
      }
      const [fromStr, toStr] = periodValue.split('_');
      const from = new Date(fromStr);
      const to = new Date(toStr);
      to.setHours(23, 59, 59, 999);
      setRange([from, to]);
      setMonitorPeriod('DAILY');
      setSelectedChatType('ALL');
    }
    setSelectedInsightPeriod(periodValue);
  };

  const handleReset = () => {
    setSelectedInsightPeriod(null);
    if (savedDefaults) {
      if (savedDefaults.range) setRange(savedDefaults.range);
      if (savedDefaults.monitorPeriod) setMonitorPeriod(savedDefaults.monitorPeriod);
      if (savedDefaults.chatType) setSelectedChatType(savedDefaults.chatType);
      setSavedDefaults(null);
    }
  };

  const contextInsightsParams: ContextInsightsParams | null = useMemo(() => {
    if (!monitorPeriod || !selectedChatType) {
      return null;
    }

    return {
      projectId: Number(projectId),
      monitorPeriod,
      chatType: selectedChatType,
      from: startDate,
      to: endDate,
      monitorPromptIds: filteredMonitorPromptIds,
      filterMonitorTypes,
    };
  }, [monitorPeriod, selectedChatType, projectId, startDate, endDate, filteredMonitorPromptIds, filterMonitorTypes]);

  const { contextInsightsResponse, isLoading: isContextInsightsLoading } =
    useGetContextInsights(contextInsightsParams);

  const periodFilterItems: FilterItem[] = useMemo(() => {
    if (!selectedChatType || !monitorPeriod) {
      return [];
    }
    const replaceLastTwoSegments = (url: string, period: string, chat: string): string => {
      const parts = url.split('/').filter(Boolean);
      const newParts = parts.slice(0, -2);
      return `/${newParts.join('/')}/${period}/${chat}`;
    };
    const chat = selectedChatType.toLowerCase();
    return [
      {
        url: replaceLastTwoSegments(location.pathname, 'daily', chat),
        label: t('dailyFilter'),
        isActive: monitorPeriod === 'DAILY',
      },
      {
        url: replaceLastTwoSegments(location.pathname, 'weekly', chat),
        label: t('weeklyFilter'),
        isActive: monitorPeriod === 'WEEKLY',
      },
      {
        url: replaceLastTwoSegments(location.pathname, 'monthly', chat),
        label: t('monthlyFilter'),
        isActive: monitorPeriod === 'MONTHLY',
      },
    ];
  }, [location.pathname, selectedChatType, monitorPeriod, t]);

  const {
    commonKeywords,
    isLoading: isCommonKeywordsLoading,
  } = useGetCommonKeywords(projectIdNumber);

  const { project } = useGetProject(projectIdNumber);

  const { insightPeriods: rawInsightPeriods } = useGetContextInsightPeriods(projectIdNumber);

  const insightPeriods = useMemo(() => {
    // Same localized "start – end (year)" format as the chart tooltips.
    const { tooltip: formatRange } = buildBucketLabelFormatters(null, i18n.language);
    return rawInsightPeriods.map((p) => {
      const start = format(new Date(p.periodStart), 'yyyy-MM-dd');
      const end = format(new Date(p.periodEnd), 'yyyy-MM-dd');
      const label = formatRange(`${start} - ${end}`);
      const value = `${start}_${end}`;
      return { label, value };
    });
  }, [rawInsightPeriods, i18n.language]);

  const exportLang = i18n.language.startsWith('pl') ? 'pl' : 'en';

  const handleExportTopicsAnalysis = useCallback(async () => {
    if (isDownloadingTopicsAnalysis || !monitorPeriod || !selectedChatType) {
      return;
    }

    setIsDownloadingTopicsAnalysis(true);
    try {
      const blob = await exportContextTopicsAnalysisCsv(
        projectIdNumber,
        selectedChatType,
        monitorPeriod,
        startDate,
        endDate,
        exportLang,
        filteredMonitorPromptIds,
        filterMonitorTypes
      );
      const fromPart = startDate ? format(startDate, 'yyyy-MM-dd') : 'from';
      const toPart = endDate ? format(endDate, 'yyyy-MM-dd') : 'to';
      downloadBlobAsFile(
        blob,
        `context-topics-analysis-${projectIdNumber}-${selectedChatType}-${monitorPeriod}-${fromPart}-${toPart}-${exportLang}.csv`
      );
    } catch (err) {
      console.error(err);
    } finally {
      setIsDownloadingTopicsAnalysis(false);
    }
  }, [
    endDate,
    exportLang,
    filterMonitorTypes,
    filteredMonitorPromptIds,
    isDownloadingTopicsAnalysis,
    monitorPeriod,
    projectIdNumber,
    selectedChatType,
    startDate,
  ]);

  const handleExportBrandPresence = useCallback(async () => {
    if (isDownloadingBrandPresence || !monitorPeriod || !selectedChatType) {
      return;
    }

    setIsDownloadingBrandPresence(true);
    try {
      const blob = await exportContextBrandPresenceCsv(
        projectIdNumber,
        selectedChatType,
        monitorPeriod,
        startDate,
        endDate,
        exportLang,
        filteredMonitorPromptIds,
        filterMonitorTypes
      );
      const fromPart = startDate ? format(startDate, 'yyyy-MM-dd') : 'from';
      const toPart = endDate ? format(endDate, 'yyyy-MM-dd') : 'to';
      downloadBlobAsFile(
        blob,
        `context-brand-presence-${projectIdNumber}-${selectedChatType}-${monitorPeriod}-${fromPart}-${toPart}-${exportLang}.csv`
      );
    } catch (err) {
      console.error(err);
    } finally {
      setIsDownloadingBrandPresence(false);
    }
  }, [
    endDate,
    exportLang,
    filterMonitorTypes,
    filteredMonitorPromptIds,
    isDownloadingBrandPresence,
    monitorPeriod,
    projectIdNumber,
    selectedChatType,
    startDate,
  ]);

  useProjectNavigate(projectIdNumber);

  if (!accountResponse || !accountResponse.addons.includes('CONTEXT_INSIGHTS') || !projectId) {
    return <Navigate to={PROJECTS} replace />;
  }

  return (
    <div className="context-analysis-page page-content">
      <div className="page-box">
        <div className="page-box__filters" data-observe-sticky>
          <Title
            isBaseCategory={false}
            title={t('contextAnalysis.header')}
            children={
              <div className="filters">
                <FilterPanel
                  baseUrl={location.pathname}
                  showDateRange={!selectedInsightPeriod}
                  showChatTypeDropdown={!selectedInsightPeriod}
                  showPeriodTabs={false}
                >
                  {groupFilterOptions.length > 0 && (
                    <Dropdown
                      value={selectedGroupId}
                      onChange={(e) => setSelectedGroupId(e.value)}
                      options={groupFilterOptions}
                      optionLabel="label"
                      optionValue="value"
                      className="dropdown dropdown--group-filter"
                      valueTemplate={(option) => (
                        <>
                          <span id={`group-filter-val-context-${option?.value}`} className="dropdown--group-filter__label">
                            {option?.label}
                          </span>
                          {option?.label && (
                            <Tooltip target={`#group-filter-val-context-${option.value}`} content={option.label} position="top" showDelay={300} />
                          )}
                        </>
                      )}
                      itemTemplate={(option) => (
                        <>
                          <span id={`group-filter-item-context-${option.value}`} className="dropdown--group-filter__label">
                            {option.label}
                          </span>
                          <Tooltip target={`#group-filter-item-context-${option.value}`} content={option.label} position="top" showDelay={300} />
                        </>
                      )}
                    />
                  )}
                  {selectedInsightPeriod && (
                    <Button
                      className="btn btn--sm btn--secondary"
                      label={t('resetToDefault')}
                      onClick={handleReset}
                      type="button"
                    />
                  )}
                </FilterPanel>
              </div>
            }
            showStatusDot={true}
            childrenOnMain={<div className="title-box__subtitle">{t('contextAnalysis.desc')}</div>}
          />
        </div>
        <div className="page-box__stats">
          <div className="stats stats--two-cols">
            <SentimentAnalysis
              dateDistribution={contextInsightsResponse?.dateDistribution || []}
              sentimentDistribution={contextInsightsResponse?.sentimentDistribution || []}
              chatTypeDistribution={contextInsightsResponse?.chatTypeDistribution || []}
              insights={contextInsightsResponse?.monitorInsightData?.aggregated?.insights}
              eventsData={monitorEvents}
              period={monitorPeriod || 'DAILY'}
              isLoading={isContextInsightsLoading}
              insightPeriods={insightPeriods}
              selectedInsightPeriod={selectedInsightPeriod}
              onInsightPeriodChange={(value) => {
                if (value === null) {
                  handleReset();
                } else {
                  handleInsightPeriodChange(value);
                }
              }}
              periodFilterItems={periodFilterItems}
            />
          </div>
          <KeyTopics
            topicsDistribution={contextInsightsResponse?.topicsDistribution || []}
            suggestedTopics={project?.suggestedTopics || []}
            keyword={contextInsightsResponse?.defaultKeyword}
            commonKeywords={commonKeywords}
            isLoading={isContextInsightsLoading}
            canExport={canExport === true}
            isDownloading={isDownloadingTopicsAnalysis}
            onExportTopicsAnalysis={handleExportTopicsAnalysis}
          />
          <BrandStrength
            topicsDistribution={contextInsightsResponse?.topicsDistribution || []}
            suggestedTopics={project?.suggestedTopics || []}
            keyword={contextInsightsResponse?.defaultKeyword}
            commonKeywords={commonKeywords}
            isLoading={isContextInsightsLoading || isCommonKeywordsLoading}
            canExport={canExport === true}
            isDownloading={isDownloadingBrandPresence}
            onExportBrandPresence={handleExportBrandPresence}
          />
          <ContextGap
            contextGapTopics={contextInsightsResponse?.contextGapTopics || []}
            suggestedTopics={project?.suggestedTopics || []}
            commonKeywords={commonKeywords}
            isLoading={isContextInsightsLoading || isCommonKeywordsLoading}
          />
          <div className="stats stats--scroll stats--full-width ">
            <TopicShare
              topicsDistribution={contextInsightsResponse?.topicsDistribution || []}
              suggestedTopics={project?.suggestedTopics || []}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContextAnalysisPage;